import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  CheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import {
  createFlowAccount,
  fetchFlowAccounts,
  deleteFlowAccount,
  updateFlowAccount,
} from 'views/finance/moduleServices/flow_requests';
import { getPluggyItems, getPluggyAccounts, syncPluggyAccounts } from 'views/finance/moduleServices/pluggy_services';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import ModalConfirm from 'views/finance/moduleComponents/ModalConfirm';

const emptyOpenfinanceDraft = () => ({
  openfinance_item: '',
  openfinance_item_name: '',
  openfinance_account: '',
  openfinance_account_name: '',
});

// ##### MANAGE ACCOUNTS + OPEN FINANCE MAPPING (single modal)
const CustomModalAccountsManage = ({ isOpen, onClose, onSuccess }) => {
  const [accounts, setAccounts] = useState([]);
  const [pluggyItems, setPluggyItems] = useState([]);
  const [pluggyAccountsMap, setPluggyAccountsMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [pluggyLoading, setPluggyLoading] = useState(false);
  const [syncingPluggy, setSyncingPluggy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);

  const [editingAccountId, setEditingAccountId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const [addForm, setAddForm] = useState({
    account_name: '',
    ...emptyOpenfinanceDraft(),
  });

  const loadPluggyAccountsMapFromDb = useCallback(async (itemsList) => {
    if (!itemsList || itemsList.length === 0) {
      setPluggyAccountsMap({});
      return;
    }
    const results = await Promise.all(
      itemsList.map(async (item) => {
        try {
          const rows = await getPluggyAccounts(item.item_id);
          return { itemId: item.item_id, accounts: rows || [] };
        } catch (e) {
          console.error(e);
          return { itemId: item.item_id, accounts: [] };
        }
      })
    );
    const map = {};
    results.forEach(({ itemId, accounts: acc }) => {
      map[itemId] = acc;
    });
    setPluggyAccountsMap(map);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setPluggyLoading(true);
    try {
      const [accountsData, itemsData] = await Promise.all([fetchFlowAccounts(), getPluggyItems()]);
      setAccounts(accountsData || []);
      const items = itemsData || [];
      setPluggyItems(items);
      await loadPluggyAccountsMapFromDb(items);
    } catch (error) {
      console.error('Error loading manage accounts modal:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setPluggyLoading(false);
    }
  }, [loadPluggyAccountsMapFromDb]);

  useEffect(() => {
    if (isOpen) {
      setEditingAccountId(null);
      setEditDraft(null);
      loadAll();
    }
  }, [isOpen, loadAll]);

  const cancelEdit = () => {
    setEditingAccountId(null);
    setEditDraft(null);
  };

  const startEdit = (account) => {
    if (editingAccountId != null && editingAccountId !== account.id) {
      cancelEdit();
    }
    setEditingAccountId(account.id);
    setEditDraft({
      id: account.id,
      openfinance_item: account.openfinance_item || '',
      openfinance_item_name: account.openfinance_item_name || '',
      openfinance_account: account.openfinance_account || '',
      openfinance_account_name: account.openfinance_account_name || '',
    });
  };

  const handleEditItemChange = (itemId) => {
    if (!itemId) {
      setEditDraft((prev) =>
        prev ? { ...prev, ...emptyOpenfinanceDraft() } : prev
      );
      return;
    }
    const selectedItem = pluggyItems.find((item) => item.item_id === itemId);
    const itemName = selectedItem ? selectedItem.institution : '';
    setEditDraft((prev) =>
      prev
        ? {
            ...prev,
            openfinance_item: itemId,
            openfinance_item_name: itemName,
            openfinance_account: '',
            openfinance_account_name: '',
          }
        : prev
    );
  };

  const handleEditAccountChange = (accountIdPluggy) => {
    setEditDraft((prev) => {
      if (!prev) return prev;
      if (!accountIdPluggy) {
        return { ...prev, openfinance_account: '', openfinance_account_name: '' };
      }
      const accountName = prev.openfinance_item
        ? pluggyAccountsMap[prev.openfinance_item]?.find((a) => a.id === accountIdPluggy)?.name || ''
        : '';
      return {
        ...prev,
        openfinance_account: accountIdPluggy,
        openfinance_account_name: accountName,
      };
    });
  };

  const saveEdit = async () => {
    if (!editDraft) return;
    setSavingEdit(true);
    try {
      await updateFlowAccount(editDraft.id, {
        openfinance_item: editDraft.openfinance_item || '',
        openfinance_item_name: editDraft.openfinance_item_name || '',
        openfinance_account: editDraft.openfinance_account || '',
        openfinance_account_name: editDraft.openfinance_account_name || '',
      });
      toast.success('Mapping saved');
      cancelEdit();
      await loadAll();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to save');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleRefreshPluggyCache = async () => {
    if (!pluggyItems.length) {
      toast.error('No Pluggy items to sync');
      return;
    }
    setSyncingPluggy(true);
    try {
      const syncResult = await syncPluggyAccounts({ sync_all: true });
      await loadPluggyAccountsMapFromDb(pluggyItems);
      const synced = syncResult?.accounts_synced ?? null;
      if (synced === 0) {
        toast.error(
          'Pluggy sync completed but returned 0 accounts. Re-link the bank item in Pluggy or check subscription tier.',
          { duration: 7000 }
        );
      } else {
        toast.success(
          synced != null
            ? `Pluggy cache updated (${synced} account(s))`
            : 'Pluggy accounts cache updated'
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || error.message || 'Sync failed');
    } finally {
      setSyncingPluggy(false);
    }
  };

  const handleAddItemChange = (itemId) => {
    if (!itemId) {
      setAddForm((prev) => ({ ...prev, ...emptyOpenfinanceDraft() }));
      return;
    }
    const selectedItem = pluggyItems.find((item) => item.item_id === itemId);
    setAddForm((prev) => ({
      ...prev,
      openfinance_item: itemId,
      openfinance_item_name: selectedItem ? selectedItem.institution : '',
      openfinance_account: '',
      openfinance_account_name: '',
    }));
  };

  const handleAddAccountChange = (accountIdPluggy) => {
    setAddForm((prev) => {
      if (!accountIdPluggy) {
        return { ...prev, openfinance_account: '', openfinance_account_name: '' };
      }
      const name = prev.openfinance_item
        ? pluggyAccountsMap[prev.openfinance_item]?.find((a) => a.id === accountIdPluggy)?.name || ''
        : '';
      return {
        ...prev,
        openfinance_account: accountIdPluggy,
        openfinance_account_name: name,
      };
    });
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.account_name?.trim()) {
      toast.error('Account name is required');
      return;
    }
    setSubmitting(true);
    try {
      await createFlowAccount({
        account_name: addForm.account_name.trim(),
        openfinance_item: addForm.openfinance_item || '',
        openfinance_item_name: addForm.openfinance_item_name || '',
        openfinance_account: addForm.openfinance_account || '',
        openfinance_account_name: addForm.openfinance_account_name || '',
      });
      toast.success('Account created');
      setAddForm({ account_name: '', ...emptyOpenfinanceDraft() });
      setShowAddForm(false);
      await loadAll();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAccount = (accountId, accountName) => {
    setAccountToDelete({ id: accountId, name: accountName });
    setShowConfirmModal(true);
  };

  const confirmDeleteAccount = async () => {
    if (!accountToDelete) return;
    setDeleting(true);
    setShowConfirmModal(false);
    try {
      await deleteFlowAccount(accountToDelete.id);
      toast.success('Account deleted');
      if (editingAccountId === accountToDelete.id) cancelEdit();
      await loadAll();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete account');
    } finally {
      setDeleting(false);
      setAccountToDelete(null);
    }
  };

  const cancelDeleteAccount = () => {
    setShowConfirmModal(false);
    setAccountToDelete(null);
  };

  const handleClose = () => {
    if (!submitting && !deleting && !showConfirmModal && !savingEdit) {
      cancelEdit();
      onClose();
      setAddForm({ account_name: '', ...emptyOpenfinanceDraft() });
      setShowAddForm(false);
    }
  };

  const formatCreated = (account) => {
    if (!account.created_at) return '';
    const d = account.created_at.split('T')[0];
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return new Date(Number(y), Number(m) - 1, Number(day)).toLocaleDateString('pt-BR');
  };

  const isMappingLinked = (account) =>
    Boolean(account.openfinance_item && account.openfinance_account);

  const resolveOpenfinanceItemLabel = (account) => {
    const itemId = (account.openfinance_item || '').trim();
    if (!itemId) return '—';
    const fromAccount = (account.openfinance_item_name || '').trim();
    if (fromAccount) return fromAccount;
    const pluggy = pluggyItems.find((i) => i.item_id === itemId);
    return (pluggy?.institution || '').trim() || '—';
  };

  const resolveOpenfinanceAccountLabel = (account) => {
    const itemId = (account.openfinance_item || '').trim();
    const accId = (account.openfinance_account || '').trim();
    if (!accId) return '—';
    const fromAccount = (account.openfinance_account_name || '').trim();
    if (fromAccount) return fromAccount;
    if (itemId) {
      const row = (pluggyAccountsMap[itemId] || []).find((a) => a.id === accId);
      if ((row?.name || '').trim()) return row.name.trim();
    }
    return '—';
  };

  /** Read-only: item / account on separate lines, name then hash (Pluggy id). */
  const mappingIdsSubtext = (account) => {
    const itemId = (account.openfinance_item || '').trim();
    const accId = (account.openfinance_account || '').trim();
    if (!itemId && !accId) return null;
    const itemName = resolveOpenfinanceItemLabel(account);
    const accName = resolveOpenfinanceAccountLabel(account);
    const lineClass = 'text-[11px] leading-snug break-all max-w-xl';
    return (
      <div className={`${lineClass} space-y-0.5 mt-0.5`}>
        <div className={lineClass}>
          <span className="text-gray-400 select-none font-sans">item: </span>
          <span className="text-gray-700 font-sans">{itemId ? itemName : '—'}</span>
          <span className="text-gray-400 select-none font-sans"> | </span>
          <span className="text-gray-500 font-mono">{itemId || '—'}</span>
        </div>
        <div className={lineClass}>
          <span className="text-gray-400 select-none font-sans">account: </span>
          <span className="text-gray-700 font-sans">{accId ? accName : '—'}</span>
          <span className="text-gray-400 select-none font-sans"> | </span>
          <span className="text-gray-500 font-mono">{accId || '—'}</span>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const blockInteraction = submitting || deleting || showConfirmModal || savingEdit;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
      <div className="bg-white rounded-2xl shadow w-full max-w-5xl max-h-[90vh] relative flex flex-col">
        {(submitting || deleting || savingEdit) && (
          <div className="absolute inset-0 flex items-center justify-center z-10 rounded-2xl bg-white/80">
            <SpinnerLoad />
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Manage accounts & Open Finance</h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={blockInteraction}
            className={`text-gray-500 ${blockInteraction ? 'opacity-50' : 'hover:text-gray-700'}`}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 min-h-0">
          {loading || pluggyLoading ? (
            <div className="flex items-center justify-center h-40">
              <SpinnerLoad />
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No accounts yet. Add one below.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {accounts.map((account) => {
                const isEditing = editingAccountId === account.id;
                const linked = isMappingLinked(account);
                const initial = (account.account_name || '?').charAt(0).toUpperCase();
                return (
                  <div
                    key={account.id}
                    className="p-3 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-xs font-medium text-blue-600">{initial}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-sm font-medium text-gray-800 truncate">
                              {account.account_name}
                            </span>
                            {formatCreated(account) ? (
                              <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                {formatCreated(account)}
                              </span>
                            ) : null}
                          </div>
                          {!isEditing && mappingIdsSubtext(account)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!isEditing &&
                          (linked ? (
                            <span className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-1 rounded whitespace-nowrap">
                              ✓ Linked
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500 whitespace-nowrap">Not configured</span>
                          ))}
                        <button
                          type="button"
                          onClick={() => startEdit(account)}
                          disabled={blockInteraction}
                          className="p-1.5 rounded-md text-green-800 hover:bg-green-100 disabled:opacity-40"
                          title="Edit Open Finance mapping"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteAccount(account.id, account.account_name)}
                          disabled={blockInteraction}
                          className="p-1.5 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-40"
                          title="Delete account"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {isEditing && (
                      <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                        <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                          Saved mapping
                        </p>
                        {mappingIdsSubtext(account) ?? (
                          <p className="text-[11px] text-gray-400">No Open Finance ids saved yet.</p>
                        )}
                        <div className="flex flex-wrap items-end gap-2">
                          <div className="min-w-[10rem] flex-1">
                            <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Open Finance item</label>
                            <select
                              value={editDraft?.openfinance_item || ''}
                              onChange={(e) => handleEditItemChange(e.target.value)}
                              className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white"
                            >
                              <option value="">—</option>
                              {pluggyItems.map((item) => (
                                <option key={item.item_id} value={item.item_id}>
                                  {item.institution} — {item.item_id}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="min-w-[10rem] flex-1">
                            <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Open Finance account</label>
                            <select
                              value={editDraft?.openfinance_account || ''}
                              onChange={(e) => handleEditAccountChange(e.target.value)}
                              disabled={!editDraft?.openfinance_item}
                              className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white disabled:bg-gray-100"
                            >
                              <option value="">—</option>
                              {editDraft?.openfinance_item &&
                                (pluggyAccountsMap[editDraft.openfinance_item] || []).map((acc) => (
                                  <option key={acc.id} value={acc.id}>
                                    {acc.name} — {acc.type}
                                    {acc.currencyCode ? ` (${acc.currencyCode})` : ''}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={saveEdit}
                              disabled={savingEdit}
                              className="flex items-center gap-1 px-2 py-1.5 bg-green-800 text-white text-xs rounded-md hover:bg-green-900 disabled:opacity-50"
                            >
                              <CheckIcon className="h-3.5 w-3.5" />
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              disabled={savingEdit}
                              className="px-2 py-1.5 border border-gray-300 text-xs rounded-md text-gray-700 hover:bg-gray-50 bg-white"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowAddForm(!showAddForm)}
              disabled={blockInteraction}
              className="flex items-center gap-2 px-4 py-2 bg-green-800 hover:bg-green-900 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4" />
              Add account
            </button>
            <button
              type="button"
              onClick={handleRefreshPluggyCache}
              disabled={blockInteraction || syncingPluggy || !pluggyItems.length}
              title="Sync account list from Pluggy API (uses quota)"
              className="flex items-center gap-1 px-4 py-2 text-sm border border-green-800 text-green-800 rounded-lg hover:bg-green-50 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${syncingPluggy ? 'animate-spin' : ''}`} />
              {syncingPluggy ? 'Syncing…' : 'Refresh Pluggy accounts'}
            </button>
          </div>

          {showAddForm && (
            <form className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3" onSubmit={handleSubmitAdd}>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="min-w-[10rem] flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Account name *</label>
                  <input
                    type="text"
                    value={addForm.account_name}
                    onChange={(e) => setAddForm((p) => ({ ...p, account_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    disabled={submitting}
                    required
                    placeholder="e.g. Nubank"
                  />
                </div>
                <div className="min-w-[10rem] flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Open Finance item</label>
                  <select
                    value={addForm.openfinance_item}
                    onChange={(e) => handleAddItemChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    disabled={submitting}
                  >
                    <option value="">— optional —</option>
                    {pluggyItems.map((item) => (
                      <option key={item.item_id} value={item.item_id}>
                        {item.institution} — {item.item_id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[10rem] flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Open Finance account</label>
                  <select
                    value={addForm.openfinance_account}
                    onChange={(e) => handleAddAccountChange(e.target.value)}
                    disabled={submitting || !addForm.openfinance_item}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-100"
                  >
                    <option value="">— optional —</option>
                    {addForm.openfinance_item &&
                      (pluggyAccountsMap[addForm.openfinance_item] || []).map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.name} — {acc.type}
                          {acc.currencyCode ? ` (${acc.currencyCode})` : ''}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  disabled={submitting}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-green-800 text-white rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {submitting ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <ModalConfirm
        isOpen={showConfirmModal}
        onClose={cancelDeleteAccount}
        onConfirm={confirmDeleteAccount}
        title="Delete account"
        message={`Delete "${accountToDelete?.name}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white shadow"
        cancelButtonClass="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100"
      />
    </div>
  );
};

export default CustomModalAccountsManage;
